import { useState, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Plus,
  Trash2,
  Upload,
  Loader2,
  FileCode,
  Key,
  HardDrive,
} from "lucide-react";

// PSADT v4 destination presets
export const DESTINATION_PRESETS = [
  { value: "$envProgramFiles", label: "$envProgramFiles  ·  C:\\Program Files" },
  { value: "$envProgramFilesX86", label: "$envProgramFilesX86  ·  C:\\Program Files (x86)" },
  { value: "$envWinDir", label: "$envWinDir  ·  C:\\Windows" },
  { value: "$envSystem32", label: "$envSystem32  ·  C:\\Windows\\System32" },
  { value: "$envProgramData", label: "$envProgramData  ·  C:\\ProgramData" },
  { value: "$envCommonDesktop", label: "$envCommonDesktop  ·  Public Desktop" },
  { value: "$envCommonStartMenuPrograms", label: "$envCommonStartMenuPrograms  ·  Common Start Menu" },
  { value: "$envAppData", label: "$envAppData  ·  per-user Roaming AppData" },
  { value: "$envLocalAppData", label: "$envLocalAppData  ·  per-user Local AppData" },
  { value: "__custom__", label: "Custom absolute path…" },
];

const PHASES = [
  { value: "pre_install", label: "Pre-Install" },
  { value: "post_install", label: "Post-Install" },
  { value: "pre_uninstall", label: "Pre-Uninstall" },
  { value: "post_uninstall", label: "Post-Uninstall" },
];

const HIVES = ["HKLM", "HKCU", "HKCR", "HKU"];
const REG_TYPES = ["String", "ExpandString", "DWord", "QWord", "MultiString", "Binary"];

// Build PSADT preview for a file entry
function previewFileLine(entry) {
  if (!entry?.filename) return "";

  const copyMethod = entry.copy_method || "install_dir";
  const sub = (entry.subpath || "").replace(/^[\\/]+/, "");

  // ---- Per-user copy: Copy-ADTFileToUserProfiles ----
  if (copyMethod === "user_profiles") {
    const destExpr = sub ? `'${sub.replace(/'/g, "''")}'` : `''`;
    const copyLine = `Copy-ADTFileToUserProfiles -Path "$($adtSession.DirFiles)\\ExpertFiles\\${entry.filename}" -Destination ${destExpr} -Recurse`;
    if (entry.remove_on_uninstall) {
      const removeJoined = sub ? `${sub.replace(/[\\/]+$/, "")}\\${entry.filename}` : entry.filename;
      const removeExpr = `'${removeJoined.replace(/'/g, "''")}'`;
      return `${copyLine}\n# On uninstall: Remove-ADTFileFromUserProfiles -Path ${removeExpr}`;
    }
    return copyLine;
  }

  // ---- Install-dir copy: Copy-ADTFile ----
  const destField = entry.destination === "__custom__" ? (entry.customPath || "") : entry.destination;
  if (!destField) return "";
  let destExpr;
  let removeJoined;
  if (destField.startsWith("$")) {
    const joined = sub ? `${destField}\\${sub}` : destField;
    destExpr = `"${joined}"`;
    removeJoined = `${joined}\\${entry.filename}`;
  } else {
    const joined = sub ? `${destField.replace(/[\\/]+$/, "")}\\${sub}` : destField;
    destExpr = `'${joined.replace(/'/g, "''")}'`;
    removeJoined = `${joined}\\${entry.filename}`;
  }
  const copyLine = `Copy-ADTFile -Path "$($adtSession.DirFiles)\\ExpertFiles\\${entry.filename}" -Destination ${destExpr} -Recurse`;
  if (entry.remove_on_uninstall) {
    const removeExpr = destField.startsWith("$")
      ? `"${removeJoined}"`
      : `'${removeJoined.replace(/'/g, "''")}'`;
    return `${copyLine}\n# On uninstall: Remove-ADTFile -LiteralPath ${removeExpr}`;
  }
  return copyLine;
}

// Build PSADT preview for a registry entry
function previewRegistryLine(entry) {
  if (!entry?.key) return "";
  const hive = (entry.hive || "HKLM").toUpperCase();
  const keyPath = (entry.key || "").replace(/^\\+/, "");
  const fullKey = `${hive}:\\${keyPath}`;
  const safeKey = fullKey.replace(/'/g, "''");
  if (entry.action === "remove") {
    if (entry.name) {
      return `Remove-ADTRegistryKey -Key '${safeKey}' -Name '${entry.name.replace(/'/g, "''")}'`;
    }
    return `Remove-ADTRegistryKey -Key '${safeKey}' -Recurse`;
  }
  const parts = [`Set-ADTRegistryKey`, `-Key '${safeKey}'`];
  if (entry.name) parts.push(`-Name '${entry.name.replace(/'/g, "''")}'`);
  const t = entry.type || "String";
  const v = entry.value ?? "";
  if (t === "DWord" || t === "QWord") {
    const n = Number(v) || 0;
    parts.push(`-Value ${n}`);
  } else if (t === "MultiString") {
    const lines = (v || "").split(/\r?\n/).filter((x) => x !== "");
    parts.push(lines.length ? `-Value @(${lines.map((x) => `'${x.replace(/'/g, "''")}'`).join(",")})` : "-Value @()");
  } else if (t === "Binary") {
    const hex = (v || "").replace(/\s+/g, "");
    parts.push(hex ? `-Value ([byte[]](${hex.match(/.{1,2}/g).map((h) => `0x${h}`).join(",")}))` : "-Value @()");
  } else {
    parts.push(`-Value '${String(v).replace(/'/g, "''")}'`);
  }
  parts.push(`-Type '${t}'`);
  return parts.join(" ");
}

/**
 * Expert Mode Customizations panel — handles user-uploaded files and registry entries.
 *
 * Props:
 *   - customFiles: state array
 *   - setCustomFiles: setter
 *   - registryEntries: state array
 *   - setRegistryEntries: setter
 *   - apiBase: base URL for API
 *   - getAuthHeaders: async () => headers
 *   - disabled: bool
 */
export default function ExpertModeCustomizations({
  customFiles,
  setCustomFiles,
  registryEntries,
  setRegistryEntries,
  apiBase,
  getAuthHeaders,
  disabled = false,
}) {
  const [uploadingIdx, setUploadingIdx] = useState(null);
  const fileInputs = useRef({});

  const addFileRow = () => {
    setCustomFiles([
      ...customFiles,
      {
        upload_id: null,
        filename: "",
        size: 0,
        destination: "$envProgramFiles",
        customPath: "",
        subpath: "",
        phase: "post_install",
        deployment_type: "install",
        // New: PSADT v4 cmdlet selector
        copy_method: "install_dir", // install_dir | user_profiles
        // New: by default also remove the file on uninstall
        remove_on_uninstall: true,
      },
    ]);
  };

  const removeFileRow = async (idx) => {
    const row = customFiles[idx];
    if (row?.upload_id) {
      // Best effort cleanup on server
      try {
        const headers = await getAuthHeaders();
        await axios.delete(`${apiBase}/packages/expert/upload-file/${row.upload_id}`, { headers });
      } catch (_) {
        /* ignore */
      }
    }
    setCustomFiles(customFiles.filter((_, i) => i !== idx));
  };

  const updateFileRow = (idx, patch) => {
    setCustomFiles(customFiles.map((r, i) => (i === idx ? { ...r, ...patch } : r)));
  };

  const triggerUpload = (idx) => {
    const input = fileInputs.current[idx];
    if (input) input.click();
  };

  const handleFileChosen = async (idx, file) => {
    if (!file) return;
    if (file.size > 50 * 1024 * 1024) {
      toast.error("File exceeds the 50 MB limit");
      return;
    }
    setUploadingIdx(idx);
    try {
      const headers = await getAuthHeaders();
      const form = new FormData();
      form.append("file", file);
      const resp = await axios.post(`${apiBase}/packages/expert/upload-file`, form, {
        headers: { ...headers, "Content-Type": "multipart/form-data" },
      });
      const { upload_id, filename, size, size_mb } = resp.data;
      // If row already had a prior upload, clean it
      const prev = customFiles[idx]?.upload_id;
      if (prev && prev !== upload_id) {
        axios
          .delete(`${apiBase}/packages/expert/upload-file/${prev}`, { headers })
          .catch(() => {});
      }
      updateFileRow(idx, { upload_id, filename, size, size_mb });
      toast.success(`Uploaded ${filename}`);
    } catch (e) {
      console.error("expert upload failed:", e);
      toast.error(e?.response?.data?.detail || "Upload failed");
    } finally {
      setUploadingIdx(null);
    }
  };

  const addRegistryRow = () => {
    setRegistryEntries([
      ...registryEntries,
      {
        hive: "HKLM",
        key: "",
        name: "",
        value: "",
        type: "String",
        action: "set",
        phase: "post_install",
        deployment_type: "install",
      },
    ]);
  };

  const updateRegistryRow = (idx, patch) => {
    setRegistryEntries(registryEntries.map((r, i) => (i === idx ? { ...r, ...patch } : r)));
  };

  const removeRegistryRow = (idx) => {
    setRegistryEntries(registryEntries.filter((_, i) => i !== idx));
  };

  return (
    <div className="space-y-4" data-testid="expert-customizations-panel">
      {/* ============ Custom Files ============ */}
      <div className="border border-slate-700 rounded-lg overflow-hidden">
        <div className="bg-slate-800/70 px-3 py-2 border-b border-slate-700 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <HardDrive className="h-4 w-4 text-orange-400" />
            <span className="text-sm font-semibold text-slate-200">Custom Files (PSADT v4 Copy-ADTFile)</span>
            {customFiles.length > 0 && (
              <Badge variant="outline" className="text-[10px] py-0 border-orange-500 text-orange-300">
                {customFiles.length}
              </Badge>
            )}
          </div>
          <Button
            type="button"
            size="sm"
            variant="outline"
            disabled={disabled}
            onClick={addFileRow}
            className="h-7 px-2 text-xs"
            data-testid="expert-add-file-btn"
          >
            <Plus className="h-3.5 w-3.5 mr-1" />
            Add file
          </Button>
        </div>

        <div className="p-3 space-y-3 bg-slate-900/40">
          {customFiles.length === 0 ? (
            <p className="text-xs text-slate-400 italic">
              No custom files. Click <strong>Add file</strong> to upload and place files alongside the install (max 50 MB each).
            </p>
          ) : (
            customFiles.map((row, idx) => (
              <div
                key={idx}
                className="border border-slate-700 rounded-md p-3 space-y-3 bg-slate-900/70"
                data-testid={`expert-file-row-${idx}`}
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <Label className="text-xs text-slate-400">File</Label>
                    <div className="flex items-center gap-2 mt-1">
                      <input
                        type="file"
                        className="hidden"
                        ref={(el) => (fileInputs.current[idx] = el)}
                        onChange={(e) => handleFileChosen(idx, e.target.files?.[0])}
                      />
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        onClick={() => triggerUpload(idx)}
                        disabled={disabled || uploadingIdx === idx}
                        className="h-8 text-xs"
                        data-testid={`expert-file-upload-btn-${idx}`}
                      >
                        {uploadingIdx === idx ? (
                          <>
                            <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> Uploading…
                          </>
                        ) : (
                          <>
                            <Upload className="h-3.5 w-3.5 mr-1" />
                            {row.filename ? "Replace" : "Choose file"}
                          </>
                        )}
                      </Button>
                      {row.filename && (
                        <span className="text-xs font-mono text-slate-200 truncate" title={row.filename}>
                          {row.filename}
                          {row.size_mb !== undefined && (
                            <span className="ml-2 text-slate-400">({row.size_mb} MB)</span>
                          )}
                        </span>
                      )}
                    </div>
                  </div>
                  <Button
                    type="button"
                    size="icon"
                    variant="ghost"
                    onClick={() => removeFileRow(idx)}
                    className="h-7 w-7 text-slate-400 hover:text-red-400"
                    data-testid={`expert-file-remove-${idx}`}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div className="sm:col-span-2">
                    <Label className="text-xs text-slate-400">Copy method</Label>
                    <Select
                      value={row.copy_method || "install_dir"}
                      onValueChange={(v) => updateFileRow(idx, { copy_method: v })}
                      disabled={disabled}
                    >
                      <SelectTrigger
                        className="h-8 text-xs bg-slate-800 border-slate-600 text-slate-200 mt-1"
                        data-testid={`expert-file-copy-method-${idx}`}
                      >
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="install_dir" className="text-xs">
                          Install location (Copy-ADTFile)
                        </SelectItem>
                        <SelectItem value="user_profiles" className="text-xs">
                          Each user profile (Copy-ADTFileToUserProfiles)
                        </SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  {row.copy_method !== "user_profiles" && (
                    <div>
                      <Label className="text-xs text-slate-400">Destination</Label>
                      <Select
                        value={row.destination}
                        onValueChange={(v) => updateFileRow(idx, { destination: v })}
                        disabled={disabled}
                      >
                        <SelectTrigger
                          className="h-8 text-xs bg-slate-800 border-slate-600 text-slate-200 mt-1"
                          data-testid={`expert-file-dest-${idx}`}
                        >
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {DESTINATION_PRESETS.map((p) => (
                            <SelectItem key={p.value} value={p.value} className="font-mono text-xs">
                              {p.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      {row.destination === "__custom__" && (
                        <Input
                          value={row.customPath || ""}
                          onChange={(e) => updateFileRow(idx, { customPath: e.target.value })}
                          placeholder="e.g. C:\\Tools\\MyApp"
                          className="h-8 text-xs font-mono bg-slate-800 border-slate-600 text-slate-200 mt-2"
                          data-testid={`expert-file-custompath-${idx}`}
                        />
                      )}
                    </div>
                  )}
                  <div>
                    <Label className="text-xs text-slate-400">Phase</Label>
                    <Select
                      value={row.phase}
                      onValueChange={(v) => updateFileRow(idx, { phase: v })}
                      disabled={disabled}
                    >
                      <SelectTrigger
                        className="h-8 text-xs bg-slate-800 border-slate-600 text-slate-200 mt-1"
                        data-testid={`expert-file-phase-${idx}`}
                      >
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {PHASES.map((p) => (
                          <SelectItem key={p.value} value={p.value} className="text-xs">
                            {p.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="sm:col-span-2">
                    <Label className="text-xs text-slate-400">
                      {row.copy_method === "user_profiles"
                        ? "Destination subfolder inside each user profile (e.g. AppData\\Local\\MyApp)"
                        : "Subfolder / target filename (optional)"}
                    </Label>
                    <Input
                      value={row.subpath || ""}
                      onChange={(e) => updateFileRow(idx, { subpath: e.target.value })}
                      placeholder={
                        row.copy_method === "user_profiles"
                          ? "AppData\\Local\\MyCompany"
                          : "e.g. MyCompany\\Configs"
                      }
                      className="h-8 text-xs font-mono bg-slate-800 border-slate-600 text-slate-200 mt-1"
                      data-testid={`expert-file-subpath-${idx}`}
                    />
                  </div>
                  {/* Per-file: remove the same file on uninstall (default ON) */}
                  <div className="sm:col-span-2 flex items-center justify-between rounded-md border border-slate-700 bg-slate-800/60 px-3 py-2">
                    <div className="min-w-0 pr-3">
                      <Label className="text-xs text-slate-200">Remove this file on uninstall</Label>
                      <p className="text-[11px] text-slate-400 mt-0.5">
                        Mirrors the copy with a{" "}
                        {row.copy_method === "user_profiles"
                          ? "Remove-ADTFileFromUserProfiles"
                          : "Remove-ADTFile"}{" "}
                        in the matching uninstall phase.
                      </p>
                    </div>
                    <button
                      type="button"
                      role="switch"
                      aria-checked={row.remove_on_uninstall !== false}
                      onClick={() =>
                        updateFileRow(idx, {
                          remove_on_uninstall: !(row.remove_on_uninstall !== false),
                        })
                      }
                      className={`relative inline-flex h-5 w-9 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none ${
                        row.remove_on_uninstall !== false ? "bg-orange-500" : "bg-slate-600"
                      }`}
                      data-testid={`expert-file-remove-uninstall-${idx}`}
                    >
                      <span
                        className={`pointer-events-none inline-block h-4 w-4 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                          row.remove_on_uninstall !== false ? "translate-x-4" : "translate-x-0"
                        }`}
                      />
                    </button>
                  </div>
                </div>

                {row.filename && (
                  <pre className="bg-black/60 text-green-300 text-[11px] font-mono p-2 rounded border border-slate-700 overflow-x-auto whitespace-pre-wrap break-all">
                    {previewFileLine(row) || "(complete the fields to preview the PSADT line)"}
                  </pre>
                )}
              </div>
            ))
          )}
        </div>
      </div>

      {/* ============ Registry Entries ============ */}
      <div className="border border-slate-700 rounded-lg overflow-hidden">
        <div className="bg-slate-800/70 px-3 py-2 border-b border-slate-700 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Key className="h-4 w-4 text-orange-400" />
            <span className="text-sm font-semibold text-slate-200">Registry Changes (Set-/Remove-ADTRegistryKey)</span>
            {registryEntries.length > 0 && (
              <Badge variant="outline" className="text-[10px] py-0 border-orange-500 text-orange-300">
                {registryEntries.length}
              </Badge>
            )}
          </div>
          <Button
            type="button"
            size="sm"
            variant="outline"
            disabled={disabled}
            onClick={addRegistryRow}
            className="h-7 px-2 text-xs"
            data-testid="expert-add-registry-btn"
          >
            <Plus className="h-3.5 w-3.5 mr-1" />
            Add entry
          </Button>
        </div>

        <div className="p-3 space-y-3 bg-slate-900/40">
          {registryEntries.length === 0 ? (
            <p className="text-xs text-slate-400 italic">
              No registry changes. Click <strong>Add entry</strong> to set or remove values.
            </p>
          ) : (
            registryEntries.map((row, idx) => (
              <div
                key={idx}
                className="border border-slate-700 rounded-md p-3 space-y-3 bg-slate-900/70"
                data-testid={`expert-registry-row-${idx}`}
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1 grid grid-cols-12 gap-2">
                    <div className="col-span-3">
                      <Label className="text-xs text-slate-400">Hive</Label>
                      <Select
                        value={row.hive}
                        onValueChange={(v) => updateRegistryRow(idx, { hive: v })}
                        disabled={disabled}
                      >
                        <SelectTrigger
                          className="h-8 text-xs bg-slate-800 border-slate-600 text-slate-200 mt-1"
                          data-testid={`expert-reg-hive-${idx}`}
                        >
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {HIVES.map((h) => (
                            <SelectItem key={h} value={h} className="font-mono text-xs">
                              {h}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="col-span-9">
                      <Label className="text-xs text-slate-400">Key path</Label>
                      <Input
                        value={row.key || ""}
                        onChange={(e) => updateRegistryRow(idx, { key: e.target.value })}
                        placeholder="SOFTWARE\\MyCompany\\MyApp"
                        className="h-8 text-xs font-mono bg-slate-800 border-slate-600 text-slate-200 mt-1"
                        data-testid={`expert-reg-key-${idx}`}
                      />
                    </div>
                  </div>
                  <Button
                    type="button"
                    size="icon"
                    variant="ghost"
                    onClick={() => removeRegistryRow(idx)}
                    className="h-7 w-7 text-slate-400 hover:text-red-400"
                    data-testid={`expert-reg-remove-${idx}`}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>

                <div className="grid grid-cols-12 gap-2">
                  <div className="col-span-3">
                    <Label className="text-xs text-slate-400">Action</Label>
                    <Select
                      value={row.action}
                      onValueChange={(v) => updateRegistryRow(idx, { action: v })}
                      disabled={disabled}
                    >
                      <SelectTrigger
                        className="h-8 text-xs bg-slate-800 border-slate-600 text-slate-200 mt-1"
                        data-testid={`expert-reg-action-${idx}`}
                      >
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="set" className="text-xs">Set value</SelectItem>
                        <SelectItem value="remove" className="text-xs">Remove</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="col-span-4">
                    <Label className="text-xs text-slate-400">Value name</Label>
                    <Input
                      value={row.name || ""}
                      onChange={(e) => updateRegistryRow(idx, { name: e.target.value })}
                      placeholder={row.action === "remove" ? "(blank = remove key)" : "e.g. Enabled"}
                      className="h-8 text-xs font-mono bg-slate-800 border-slate-600 text-slate-200 mt-1"
                      data-testid={`expert-reg-name-${idx}`}
                    />
                  </div>
                  <div className="col-span-2">
                    <Label className="text-xs text-slate-400">Type</Label>
                    <Select
                      value={row.type}
                      onValueChange={(v) => updateRegistryRow(idx, { type: v })}
                      disabled={disabled || row.action === "remove"}
                    >
                      <SelectTrigger
                        className="h-8 text-xs bg-slate-800 border-slate-600 text-slate-200 mt-1"
                        data-testid={`expert-reg-type-${idx}`}
                      >
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {REG_TYPES.map((t) => (
                          <SelectItem key={t} value={t} className="font-mono text-xs">
                            {t}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="col-span-3">
                    <Label className="text-xs text-slate-400">Phase</Label>
                    <Select
                      value={row.phase}
                      onValueChange={(v) => updateRegistryRow(idx, { phase: v })}
                      disabled={disabled}
                    >
                      <SelectTrigger
                        className="h-8 text-xs bg-slate-800 border-slate-600 text-slate-200 mt-1"
                        data-testid={`expert-reg-phase-${idx}`}
                      >
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {PHASES.map((p) => (
                          <SelectItem key={p.value} value={p.value} className="text-xs">
                            {p.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {row.action === "set" && (
                  <div>
                    <Label className="text-xs text-slate-400">Value</Label>
                    {row.type === "MultiString" ? (
                      <Textarea
                        value={row.value || ""}
                        onChange={(e) => updateRegistryRow(idx, { value: e.target.value })}
                        placeholder="one string per line"
                        className="text-xs font-mono bg-slate-800 border-slate-600 text-slate-200 mt-1 min-h-[60px]"
                        data-testid={`expert-reg-value-${idx}`}
                      />
                    ) : (
                      <Input
                        value={row.value || ""}
                        onChange={(e) => updateRegistryRow(idx, { value: e.target.value })}
                        placeholder={
                          row.type === "DWord" || row.type === "QWord"
                            ? "0 / 1 / numeric"
                            : row.type === "Binary"
                            ? "hex bytes e.g. DEADBEEF"
                            : "value data"
                        }
                        className="h-8 text-xs font-mono bg-slate-800 border-slate-600 text-slate-200 mt-1"
                        data-testid={`expert-reg-value-${idx}`}
                      />
                    )}
                  </div>
                )}

                {row.key && (
                  <pre className="bg-black/60 text-green-300 text-[11px] font-mono p-2 rounded border border-slate-700 overflow-x-auto whitespace-pre-wrap break-all">
                    {previewRegistryLine(row) || "(complete the fields to preview the PSADT line)"}
                  </pre>
                )}
              </div>
            ))
          )}
        </div>
      </div>

      <p className="text-[11px] text-slate-500 italic flex items-center gap-1">
        <FileCode className="h-3 w-3" />
        Entries are injected into <span className="font-mono">Invoke-AppDeployToolkit.ps1</span> at the matching PSADT&nbsp;v4 phase marker.
      </p>
    </div>
  );
}

/**
 * Build the wire payload (server-side schema) from a list of file/registry rows.
 */
export function buildExpertPayload(customFiles, registryEntries) {
  const files = (customFiles || [])
    .filter((r) => r && r.upload_id && r.filename)
    .map((r) => {
      const copyMethod = r.copy_method || "install_dir";
      // For user_profiles copies, destination is unused — the subpath is the
      // path inside each profile. We still send a destination string so the
      // backend Pydantic model is satisfied.
      const destination =
        copyMethod === "user_profiles"
          ? ""
          : r.destination === "__custom__"
          ? r.customPath || ""
          : r.destination;
      return {
        upload_id: r.upload_id,
        filename: r.filename,
        destination,
        subpath: r.subpath || null,
        phase: r.phase || "post_install",
        deployment_type: r.deployment_type || "install",
        copy_method: copyMethod,
        // Default to true for any row that doesn't explicitly set the flag.
        remove_on_uninstall: r.remove_on_uninstall !== false,
      };
    })
    .filter((r) => r.copy_method === "user_profiles" || r.destination);

  const regs = (registryEntries || [])
    .filter((r) => r && r.key)
    .map((r) => ({
      hive: r.hive || "HKLM",
      key: r.key,
      name: r.name || null,
      value: r.action === "remove" ? null : (r.value ?? ""),
      type: r.type || "String",
      action: r.action || "set",
      phase: r.phase || "post_install",
      deployment_type: r.deployment_type || "install",
    }));

  return { custom_files: files, registry_entries: regs };
}
